import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useLogin } from '../../hooks/useAuth';
import { useAuthStore } from '../../store/authStore';
import { authService } from '../../api/services/authService';
import { LoginDTO } from '../../../../shared/types';

const loginSchema = z.object({
  username: z.string().min(1, 'Username is required'),
  password: z.string().min(1, 'Password is required'),
});

type Step = 'credentials' | 'setup' | 'verify';

export const Login: React.FC = () => {
  const navigate = useNavigate();
  const setAuth = useAuthStore((state) => state.setAuth);
  const { mutate: login, isPending, data: loginData } = useLogin();

  const [step, setStep] = useState<Step>('credentials');
  const [pendingToken, setPendingToken] = useState('');
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [totpSecret, setTotpSecret] = useState('');
  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);

  const codeInputRef = useRef<HTMLInputElement>(null);

  const { register, handleSubmit, formState: { errors } } = useForm<LoginDTO>({
    resolver: zodResolver(loginSchema),
  });

  // React to step-1 response
  useEffect(() => {
    if (!loginData?.requires2FA) return;
    setPendingToken(loginData.pendingToken!);
    if (loginData.requiresSetup) {
      loadSetup(loginData.pendingToken!);
    } else {
      setStep('verify');
      setTimeout(() => codeInputRef.current?.focus(), 50);
    }
  }, [loginData]);

  const loadSetup = async (pt: string) => {
    try {
      const { qrCodeUrl: url, secret } = await authService.setup2FA(pt);
      setQrCodeUrl(url);
      setTotpSecret(secret);
      setStep('setup');
      setTimeout(() => codeInputRef.current?.focus(), 50);
    } catch {
      toast.error('Failed to load 2FA setup. Please log in again.');
      setStep('credentials');
    }
  };

  const handleVerify = async () => {
    if (code.length !== 6) { toast.error('Please enter your 6-digit code'); return; }
    setVerifying(true);
    try {
      const result = await authService.verify2FA(pendingToken, code);
      setAuth(result.user!, result.token!);
      toast.success('Login successful!');
      navigate('/');
    } catch (err: any) {
      toast.error(err.message || 'Invalid code. Please try again.');
      setCode('');
      codeInputRef.current?.focus();
    } finally {
      setVerifying(false);
    }
  };

  const inputCls = 'block w-full rounded-md bg-white/5 px-3 py-1.5 text-base text-white outline-1 -outline-offset-1 outline-white/10 placeholder:text-gray-500 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-500 sm:text-sm/6';
  const btnCls = 'flex w-full justify-center rounded-md bg-indigo-500 px-3 py-1.5 text-sm/6 font-semibold text-white hover:bg-indigo-400 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed';

  return (
    <div className="flex min-h-full flex-col justify-center px-6 py-12 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-sm">
        <img alt="CalTrend" src="/logo-white-new.png" className="mx-auto h-12 w-auto hidden dark:block" />
        <img alt="CalTrend" src="/logo-color.png" className="mx-auto h-12 w-auto block dark:hidden" />
        <h2 className="mt-8 text-center text-2xl/9 font-bold tracking-tight text-white">
          Sign in to Atlas
        </h2>
      </div>

      <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">

        {/* ── Step 1: Credentials ── */}
        {step === 'credentials' && (
          <form onSubmit={handleSubmit((data) => login(data))} className="space-y-6">
            <div>
              <label htmlFor="username" className="block text-sm/6 font-medium text-gray-100">Username</label>
              <div className="mt-2">
                <input id="username" type="text" autoComplete="username" {...register('username')} className={inputCls} />
                {errors.username && <p className="mt-1 text-sm text-red-400">{errors.username.message}</p>}
              </div>
            </div>
            <div>
              <label htmlFor="password" className="block text-sm/6 font-medium text-gray-100">Password</label>
              <div className="mt-2">
                <input id="password" type="password" autoComplete="current-password" {...register('password')} className={inputCls} />
                {errors.password && <p className="mt-1 text-sm text-red-400">{errors.password.message}</p>}
              </div>
            </div>
            <button type="submit" disabled={isPending} className={btnCls}>
              {isPending ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Signing in...
                </span>
              ) : 'Sign in'}
            </button>
          </form>
        )}

        {/* ── Step 2: First-time 2FA setup ── */}
        {step === 'setup' && (
          <div className="space-y-6">
            <div className="text-center">
              <p className="text-sm text-gray-300 mb-4">
                Two-factor authentication is required for your account.<br />
                Scan this QR code with <strong className="text-white">Google Authenticator</strong>.
              </p>
              {qrCodeUrl && (
                <img src={qrCodeUrl} alt="2FA QR Code" className="mx-auto w-48 h-48 rounded-lg bg-white p-2" />
              )}
              {totpSecret && (
                <p className="mt-3 text-xs text-gray-400">
                  Can't scan? Manual key: <span className="font-mono text-gray-200 select-all">{totpSecret}</span>
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm/6 font-medium text-gray-100">
                Enter the 6-digit code to confirm setup
              </label>
              <div className="mt-2">
                <input
                  ref={codeInputRef}
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  onKeyDown={(e) => e.key === 'Enter' && handleVerify()}
                  placeholder="000000"
                  className={`${inputCls} text-center tracking-widest text-lg`}
                />
              </div>
            </div>
            <button onClick={handleVerify} disabled={verifying || code.length !== 6} className={btnCls}>
              {verifying ? 'Verifying...' : 'Confirm & Sign in'}
            </button>
            <button onClick={() => { setStep('credentials'); setCode(''); }} className="w-full text-sm text-gray-400 hover:text-gray-200 text-center">
              ← Back to login
            </button>
          </div>
        )}

        {/* ── Step 3: Ongoing 2FA verify ── */}
        {step === 'verify' && (
          <div className="space-y-6">
            <div className="text-center">
              <p className="text-sm text-gray-300">
                Open <strong className="text-white">Google Authenticator</strong> and enter<br />
                your 6-digit code for <strong className="text-white">Atlas - CalTrend</strong>.
              </p>
            </div>
            <div>
              <label className="block text-sm/6 font-medium text-gray-100">Verification code</label>
              <div className="mt-2">
                <input
                  ref={codeInputRef}
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  onKeyDown={(e) => e.key === 'Enter' && handleVerify()}
                  placeholder="000000"
                  className={`${inputCls} text-center tracking-widest text-lg`}
                  autoFocus
                />
              </div>
            </div>
            <button onClick={handleVerify} disabled={verifying || code.length !== 6} className={btnCls}>
              {verifying ? 'Verifying...' : 'Verify & Sign in'}
            </button>
            <button onClick={() => { setStep('credentials'); setCode(''); }} className="w-full text-sm text-gray-400 hover:text-gray-200 text-center">
              ← Back to login
            </button>
          </div>
        )}

      </div>
    </div>
  );
};
